/**
 * 事件筛选内容（AB 实验用）
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import classNames from 'classnames';
import { Button, Form, Select, SelectOptionWithGroup } from 'tea-component';
import { LANGUAGE_EN } from '@home/_config/config';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { CUSTOM_VALUE } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/components/EventFilter/components/EventFilterContent/config';
import EventNameSelect from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/components/EventFilter/components/EventFilterContent/components/EventNameSelect';
import EventParameterPanel from '../EventParameterPanel';
import HoursSinceRegisterSelect from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/components/EventFilter/components/EventFilterContent/components/HoursSinceRegisterSelect';
import type { RegionSelectValueType } from '@home/components/NewRegionSelect/type';
import NumberOperatorValue from '@/pages/datalab/components/NumberOperatorValue';
import { EventFilterNumberOptions } from '@/pages/datalab/components/SortableDrag/configration';
import type {
  IEventFilterItem,
  IEventPanelProps,
  TEventOccurDisplayMode,
  TOccurCountOperator,
} from './type';
import { generateEventTitle } from './utils';
import { getName } from '@home/utils/common';
import { IEventParameterItem } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/components/EventFilter/components/EventFilterContent/type';

import './index.scss';

/** 事件参数默认值（市场/地区未选时的空值） */
const DEFAULT_PARAMETER_VALUE: RegionSelectValueType = {
  type: 'market',
  regions: [],
  markets: [],
};

/**
 * 事件筛选面板：选择事件名、事件参数、注册后时长、发生次数/统计方式，用于 AB 实验事件条件配置。
 */
const EventPanel: React.FC<IEventPanelProps> = (props) => {
  const {
    value,
    onChange,
    onConfirm,
    onCancel,
    hoursSinceRegisterOptions = [],
    data,
    displayConfig,
    eventOccurOptions: eventOccurOptionsProp = [],
    placeholderTitle,
  } = props;

  const {
    eventName = '',
    eventParameter = DEFAULT_PARAMETER_VALUE,
    hoursSinceRegister = '',
    hoursSinceRegisterCustom = '',
    aggregationType = '',
    occurCountOperator = '' as TOccurCountOperator | '',
    occurCountValue = [],
  } = value ?? {};

  const { getLocalText, isEN } = useLocal('filter');
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === LANGUAGE_EN;

  /** 事件发生/事件发生次数互斥展示模式，默认仅展示统计值下拉 */
  const eventOccurDisplay: TEventOccurDisplayMode = displayConfig?.eventOccurDisplay ?? 'count';
  const showAggregation = eventOccurDisplay === 'aggregation';
  const showOccurCount = eventOccurDisplay === 'count';
  /** 仅用户类型=新用户时展示「注册后时长」Hours Since Register */
  const showHoursSinceRegister = displayConfig?.showHoursSinceRegister !== false;
  // 是否有数据
  const hasData = data && data.length > 0;
  // 事件名为空
  const isEventNameEmpty = !value?.eventName;

  const eventNameOptions = useMemo(() => {
    if (!hasData) return [];
    return data?.map(item => {
      return {
        value: item?.code,
        text: getName(isEN, item?.name_en, item?.name_ch),
        textEn: item?.name_en,
        textCN: item?.name_ch,
        groupKey: "eventName",
      };
    }) || [];
  }, [isEN, data, hasData]);

  const eventParameterOptions = useMemo(() => {
    if (!hasData && isEventNameEmpty) return [];
    const selectedEvent = data?.find((item) => item?.code === value?.eventName);
    if (!selectedEvent?.json_str) return [];
    let parameters: IEventParameterItem[] = [];
    try {
      parameters = JSON.parse(selectedEvent.json_str);
    } catch {
      return [];
    }
    return parameters?.map((param) => {
      return {
        value: param?.param_key,
        text: getName(isEN, param?.param_name_en, param?.param_name_ch),
        textEn: param?.param_name_en,
        textCN: param?.param_name_ch,
        type: param?.param_type || '',
        children: param?.options?.map((option) => {
          return {
            // 这边不可以直接用后端给的 code，会重复，当传回去的时候需要再次处理
            value: `${param?.param_key}@__@${option.code}`,
            text: getName(isEN, option?.name_en, option?.name_ch),
            textEn: option?.name_en,
            textCN: option?.name_ch,
          };
        }),
      };
    }) || [];
  }, [data, value?.eventName, hasData, isEventNameEmpty, isEN]);

  /** 切换事件名时重置事件参数，避免参数与事件不匹配 */
  const handleEventNameChange = useCallback((val: string) => {
    onChange(Object.assign({}, value, { eventName: val, eventParameter: DEFAULT_PARAMETER_VALUE }));
  }, [value, onChange]);

  const handleEventParameterChange = useCallback((newValue: RegionSelectValueType | null) => {
    onChange({ ...value, eventParameter: newValue || DEFAULT_PARAMETER_VALUE });
  }, [value, onChange]);

  /** 选非自定义时清空自定义小时数 */
  const handleHoursSinceRegisterChange = useCallback((val: string) => {
    if (val === CUSTOM_VALUE) {
      onChange({ ...value, hoursSinceRegister: val });
    } else {
      onChange({ ...value, hoursSinceRegister: val, hoursSinceRegisterCustom: '' });
    }
  }, [value, onChange]);

  const handleHoursSinceRegisterCustomChange = useCallback((val: string) => {
    onChange({ ...value, hoursSinceRegisterCustom: val, hoursSinceRegister: CUSTOM_VALUE });
  }, [value, onChange]);

  const handleAggregationTypeChange = useCallback(
    (val: string) => {
      onChange({ ...value, aggregationType: (val || '') as IEventFilterItem['aggregationType'] });
    },
    [value, onChange]
  );

  const handleOccurCountChange = useCallback(
    (op: string, val: string[]) => {
      onChange({
        ...value,
        occurCountOperator: op as TOccurCountOperator,
        occurCountValue: val,
      });
    },
    [value, onChange]
  );

  /** 自定义「注册后时长」是否超过 168 小时（7 天）上限 */
  const hasHoursMaxExceeded = useMemo(() => {
    if (hoursSinceRegister !== CUSTOM_VALUE || !hoursSinceRegisterCustom) return false;
    const customHours = Number(hoursSinceRegisterCustom);
    return Number.isFinite(customHours) && customHours > 168;
  }, [hoursSinceRegister, hoursSinceRegisterCustom]);

  /**
   * 发生次数值格式错误：between 时要求两值均为整数且右值不小于左值；其他运算符要求单值为整数。
   */
  const hasOccurCountError = useMemo(() => {
    if (!occurCountOperator) return false;
    const vals = occurCountValue || [];
    if (occurCountOperator === 'between') {
      const v0 = vals[0] ?? '';
      const v1 = vals[1] ?? '';
      const isInt0 = !v0 || Number.isInteger(Number(v0));
      const isInt1 = !v1 || Number.isInteger(Number(v1));
      if (!isInt0 || !isInt1) return true;
      const n0 = parseFloat(v0 || '0');
      const n1 = parseFloat(v1 || '');
      if (v1 && !Number.isNaN(n0) && !Number.isNaN(n1) && n1 < n0) return true;
      return false;
    }
    const v0 = vals[0] ?? '';
    return v0 ? !Number.isInteger(Number(v0)) : false;
  }, [occurCountOperator, occurCountValue]);


  /** 事件参数必填：已选事件名时，需至少选择区域或市场之一 */
  // const isEventParameterEmpty = useMemo(() => {
  //   if (!eventName) return false;
  //   const param = eventParameter ?? DEFAULT_PARAMETER_VALUE;
  //   const hasRegions = (param.regions?.length ?? 0) > 0;
  //   const hasMarkets = (param.markets?.length ?? 0) > 0;
  //   return !hasRegions && !hasMarkets;
  // }, [eventName, eventParameter]);

  /**
   * 表单不可提交：事件名为空、注册后时长未填/超限、发生次数未填或格式错误、统计方式未选等任一项成立即禁用确认。
   * 事件参数为非必填。发生次数必填：非 between 需填第一个值，between 需同时填两个值。
   */
  const isFormInvalid = useMemo(() => {
    const isEventNameEmpty = !eventName;
    const isHoursEmpty = showOccurCount && showHoursSinceRegister && (
      !hoursSinceRegister ||
      (hoursSinceRegister === CUSTOM_VALUE && !hoursSinceRegisterCustom)
    );
    const isAggregationTypeEmpty = showAggregation && !aggregationType;
    const isOccurCountInvalid = showOccurCount && hasOccurCountError;
    const vals = occurCountValue || [];
    const isOccurCountEmpty = showOccurCount && (
      !occurCountOperator ||
      (occurCountOperator === 'between'
        ? !String(vals[0] ?? '').trim() || !String(vals[1] ?? '').trim()
        : !String(vals[0] ?? '').trim())
    );
    return isEventNameEmpty || isHoursEmpty || isAggregationTypeEmpty ||
      (showOccurCount && showHoursSinceRegister && hasHoursMaxExceeded) || isOccurCountInvalid || isOccurCountEmpty;
  }, [eventName, hoursSinceRegister, hoursSinceRegisterCustom, showAggregation, showOccurCount, showHoursSinceRegister, aggregationType, occurCountOperator, occurCountValue, hasHoursMaxExceeded, hasOccurCountError]);

  /** 传入 isEn 分别用对应语言的label文案生成英文/中文标题 */
  const { currentTitle, displayNameEn, displayNameCn } = useMemo(() => {
    const displayNameEn = generateEventTitle({
      value,
      isEn: true,
      displayConfig,
      eventNameOptions,
      eventParameterOptions,
      eventOccurOptions: eventOccurOptionsProp,
      hoursSinceRegisterLabel: "Hours Since Register(h)",
      eventOccurLabel: "Event Occur",
      eventOccurCountLabel: "Event Occur Count",
      placeholder: 'Please select'
    });
    const displayNameCn = generateEventTitle({
      value,
      isEn: false,
      displayConfig,
      eventNameOptions,
      eventParameterOptions,
      eventOccurOptions: eventOccurOptionsProp,
      hoursSinceRegisterLabel: "注册时长(h)",
      eventOccurLabel: "事件发生",
      eventOccurCountLabel: "事件发生次数",
      placeholder: '请选择'
    });
    const currentTitle = getName(isEn, displayNameEn, displayNameCn);
    return { currentTitle, displayNameEn, displayNameCn };
  }, [
    value,
    eventNameOptions,
    eventParameterOptions,
    displayConfig,
    eventOccurOptionsProp,
    isEn,
  ]);

  const handleConfirm = useCallback(() => {
    if (isFormInvalid) return;
    if (onConfirm) {
      onConfirm({ ...value, displayNameEn, displayNameCn });
    }
  }, [value, displayNameEn, displayNameCn, onConfirm, isFormInvalid]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  /** 编辑过程中同步 displayNameEn/displayNameCn 到外部；若外部已重命名（en/cn 相同且与生成值不同）则不覆盖 */
  useEffect(() => {
    if (!value?.eventName || currentTitle === getLocalText('please_select')) {
      return;
    }
    const isSameAsGenerated =
      value.displayNameEn === displayNameEn && value.displayNameCn === displayNameCn;
    if (isSameAsGenerated) {
      return;
    }
    const isUserRenamed =
      value.displayNameEn !== undefined &&
      value.displayNameEn !== null &&
      value.displayNameEn === value.displayNameCn &&
      (value.displayNameEn !== displayNameEn || value.displayNameCn !== displayNameCn);
    if (isUserRenamed) {
      return;
    }
    onChange?.({ ...value, displayNameEn, displayNameCn });
  }, [value, displayNameEn, displayNameCn, currentTitle, getLocalText, onChange]);

  const placeholderText = getLocalText('please_select');
  const isPlaceholderState = currentTitle === placeholderText;
  const titleToShow = isPlaceholderState && placeholderTitle !== undefined ? placeholderTitle : currentTitle;
  return (
    <div className="dbl-event-panel">
      <div
        className={classNames('dbl-event-panel__title', {
          'dbl-event-panel__title--placeholder': isPlaceholderState,
        })}
      >
        {titleToShow}
      </div>
      <Form
        className={classNames('dbl-event-panel__form', {
          'dbl-event-panel__form--en': isEN,
        })}
      >
        <Form.Item label={getLocalText('event_name')} required>
          <EventNameSelect
            value={eventName || ''}
            onChange={handleEventNameChange}
            groups={{ eventName: getLocalText('event_name') }}
            options={eventNameOptions}
            placeholder={getLocalText('please_select')}
            selectProps={{
              listHeight: 180,
              overlayClassName: 'dbd-event-filter-content__name-select__overlay dbl-event-filter-content__name-select__overlay',
              searchable: true,
              filter: (inputValue: string, option: SelectOptionWithGroup) => {
                const keyword = String(inputValue ?? '').toLowerCase().trim();
                if (!keyword) return true;
                const opt = option as SelectOptionWithGroup & { textEn?: string; textCN?: string; };
                const text = String(opt?.text ?? '').toLowerCase();
                const textEn = String(opt?.textEn ?? '').toLowerCase();
                const textCN = String(opt?.textCN ?? '').toLowerCase();
                const val = String(opt?.value ?? '').toLowerCase();
                return text.includes(keyword) || textEn.includes(keyword) || textCN.includes(keyword) || val.includes(keyword);
              },
            }}
          />
        </Form.Item>
        <Form.Item label={getLocalText('event_parameter')}>
          {/* 切换事件名后须重挂载：NewRegionSelect 左侧选中态仅在 loading 变化时初始化，不重挂载会沿用旧事件的 selectedRegion */}
          <EventParameterPanel
            key={eventName || '__no_event__'}
            value={eventParameter || DEFAULT_PARAMETER_VALUE}
            onChange={handleEventParameterChange}
            options={eventParameterOptions}
            locale={locale}
            language={{
              name: getLocalText('event_parameter'),
              lftName: getLocalText('event_parameter'),
              rtName: getLocalText('please_select_value'),
            }}
            placeholder={getLocalText('please_select')}
            firstLevelTitle={getLocalText('event_parameter')}
          />
        </Form.Item>
        {showOccurCount && (
          <>
            {showHoursSinceRegister && (
              <Form.Item label={getLocalText('hours_since_register')} required>
                <HoursSinceRegisterSelect
                  value={hoursSinceRegister || ''}
                  customValue={hoursSinceRegisterCustom || ''}
                  onChange={handleHoursSinceRegisterChange}
                  onCustomChange={handleHoursSinceRegisterCustomChange}
                  options={hoursSinceRegisterOptions}
                  placeholder={getLocalText('please_select_value')}
                />
              </Form.Item>
            )}
            <Form.Item
              label={formatMessage({ id: 'dbl_ab_experiment_event_occur_count_label' })}
              required
              status={hasOccurCountError ? 'error' : undefined}
              message={hasOccurCountError ? formatMessage({ id: 'dbl_ab_experiment_event_occur_count_error' }) : undefined}
            >
              <NumberOperatorValue
                operator={occurCountOperator}
                value={occurCountValue}
                onChange={handleOccurCountChange}
                operatorOptions={EventFilterNumberOptions}
                valuePlaceholder={formatMessage({ id: 'dbl_filter_number' })}
                operatorPlaceholder={formatMessage({ id: 'dbl_ab_experiment_event_occur_operator_placeholder' })}
                isEn={isEn}
                error={hasOccurCountError}
              />
            </Form.Item>
          </>
        )}
        {showAggregation && (
          <Form.Item label={formatMessage({ id: 'dbl_ab_experiment_event_occur_label' })} required>
            <Select
              appearance="button"
              className="dbl-event-occur-select"
              value={aggregationType}
              options={eventOccurOptionsProp}
              placeholder={formatMessage({ id: 'dbl_ab_experiment_event_occur_placeholder' })}
              onChange={(val) => handleAggregationTypeChange(val ?? '')}
            />
          </Form.Item>
        )}
      </Form>
      <div className="dbl-event-panel__footer">
        <Button type="primary" onClick={handleConfirm} disabled={isFormInvalid}>
          {getLocalText('confirm')}
        </Button>
        <Button type="weak" onClick={handleCancel}>
          {getLocalText('cancel')}
        </Button>
      </div>
    </div>
  );
};

export default EventPanel;
